/**
 * "N unresolved suggestions from the other party" — the warning shown before
 * sharing or granting access.
 *
 * ## Why this replaces the old implementation
 *
 * Both call sites (send, grant access) counted suggestions by reaching into
 * CKEditor's plugin registry:
 *
 *     window.editor.plugins.get('TrackChanges').getSuggestions()
 *       .filter(s => s.isInContent)
 *       .filter(s => s.attributes.authorRole !== userRole)
 *
 * Under the DOCX-native editor `window.editor` is undefined, so the expression
 * short-circuits to `undefined`, `getPendingSuggestionsAlert()` returns null,
 * and the warning silently never appears — the user shares a document with
 * unresolved counterparty changes and is told nothing.
 *
 * This reads the SAME relational `document_suggestions` rows the backend gates
 * on (`Document#change_suggestions_complete?`,
 * `PrepareToSignValidatorService#no_pending_suggestions?`), which the
 * DOCX-native editor keeps in sync via `useDocxSuggestionSync`. One source of
 * truth for the warning and the gate, so they can no longer disagree.
 */

import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { getDocumentById } from '../../../../../api/queries_v2';

interface SuggestionRow {
  id: string;
  externalId?: string | null;
  status?: string | null;
  authorRole?: string | null;
}

export interface PendingCounterpartySuggestions {
  /** Pending suggestions authored by the OTHER party. */
  fromCounterparty: number;
  /** Pending suggestions from anyone — what the server's gates count. */
  total: number;
}

/**
 * @param documentId  Document to inspect.
 * @param userRole    Current user's role ('creator' | 'receiver'). Rows whose
 *                    `authorRole` differs are the counterparty's.
 */
export function usePendingCounterpartySuggestions(
  documentId?: string,
  userRole?: string | null
): PendingCounterpartySuggestions {
  const { data } = useQuery(getDocumentById, {
    variables: { documentId: String(documentId) },
    skip: !documentId,
    // The warning must reflect what the SERVER will see when the mutation
    // lands, so prefer the network but render immediately from cache.
    fetchPolicy: 'cache-and-network',
  });

  return useMemo(() => {
    const rows: SuggestionRow[] =
      (data?.getDocumentById as Record<string, any> | undefined)?.documentSuggestions ?? [];

    const pending = rows.filter((r) => String(r?.status) === 'pending');

    return {
      total: pending.length,
      fromCounterparty: userRole
        ? pending.filter((r) => r?.authorRole && r.authorRole !== userRole).length
        : pending.length,
    };
  }, [data, userRole]);
}
